export function resolveLinkedAvatarModelId(
  explicitAvatarModelId?: string | null,
  avatarConfig?: Record<string, unknown> | null
): string | null {
  const normalizedExplicitId = typeof explicitAvatarModelId === 'string' ? explicitAvatarModelId.trim() : '';
  if (normalizedExplicitId) {
    return normalizedExplicitId;
  }

  if (!avatarConfig) {
    return null;
  }

  const directAvatarModelId = typeof avatarConfig.avatarModelId === 'string' ? avatarConfig.avatarModelId.trim() : '';
  if (directAvatarModelId) {
    return directAvatarModelId;
  }

  const directModelId = typeof avatarConfig.modelId === 'string' ? avatarConfig.modelId.trim() : '';
  if (directModelId) {
    return directModelId;
  }

  const appearance = avatarConfig.appearance && typeof avatarConfig.appearance === 'object'
    ? avatarConfig.appearance as Record<string, unknown>
    : null;
  const appearanceModelId = typeof appearance?.modelId === 'string' ? appearance.modelId.trim() : '';
  return appearanceModelId || null;
}

export function resolveTtsBaseUrl(explicitTtsUrl?: string | null): string {
  const normalizedExplicitUrl = typeof explicitTtsUrl === 'string' ? explicitTtsUrl.trim().replace(/\/$/, '') : '';
  if (normalizedExplicitUrl) {
    return normalizedExplicitUrl;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.origin.replace(/\/$/, '');
}

/**
 * Decide whether the visual-channel WebSocket should auto-open as soon
 * as the avatar mounts (`true`), wait for a user gesture (`false`), or
 * delegate to `useHostedLiveState`'s default heuristic (`undefined`).
 *
 * Three input shapes:
 *
 *   1. `autoOpenChannel: true` — host explicitly opted into auto-open.
 *      This is the AgentLink / Decision Memory full-screen shape:
 *      the avatar IS the page, so opening the WS immediately means the
 *      bot's first prompt + blendshapes can stream in the background.
 *      Browser-security gestures (mic, audio.play) are still gated to
 *      the user's Listen tap — only the WebSocket itself opens early.
 *
 *   2. `requestedLiveSessionKind === 'contextual-chat'` (legacy default)
 *      — gate WS open on user gesture. Right behaviour for builder
 *      embeds (Architect Contextual Chat panel, Cara, Cross-Channel
 *      Simulator) where the avatar is one of many panels and opening
 *      five WebSockets per page load would be wasteful.
 *
 *   3. everything else — return `undefined` and let `useHostedLiveState`
 *      derive from `liveModeActive + handoffToken + !liveVoiceCallEnabled`.
 *      This is what voice-call hosts (CamlinVoice runtime, runtime-host
 *      `/visual` route) need.
 *
 * Pure function so the contract is unit-testable without React. Locked
 * down Apr-30 2026 (live-mode reliability fix). The pre-fix behaviour
 * (`requestedLiveSessionKind === 'contextual-chat'` always returns
 * `false`) was the silent reason AgentLink's first Listen click sat
 * doing nothing — the WS never auto-opened, `sendReady()` no-op'd
 * because `linked === false`, and the user had to click twice.
 */
export function resolveInitialLiveConnectRequested(args: {
  autoOpenChannel: boolean | undefined;
  requestedLiveSessionKind: string | undefined;
}): boolean | undefined {
  if (args.autoOpenChannel) {
    return true;
  }
  if (args.requestedLiveSessionKind === 'contextual-chat') {
    return false;
  }
  return undefined;
}

/**
 * Decide whether `requestLiveSession()` should attempt a SIP/WebRTC
 * voice call (`true`) or stay on the websocket-only path (`false`).
 *
 * The legacy default — `withVoiceCall = !isVisualOnly` — was wrong for
 * the AgentLink-shape host (browser-only contextual chat:
 * `isVisualOnly=false`, no `liveDestination` configured): the caller
 * would default to `true`, the SIP-init branch would hit
 * "Live mode voice destination is not configured" and bail with
 * `setLiveConnectRequested(false)`, leaving the visual channel closed
 * and the user stuck on a Listen button that does nothing.
 *
 * Honour an explicit caller override when supplied; otherwise infer
 * from `isVisualOnly` AND the presence of a `liveDestination`:
 *   - Visual IVR        (isVisualOnly=true,  liveDestination empty): no voice
 *   - AgentLink / DM    (isVisualOnly=false, liveDestination empty): no voice (browser-only)
 *   - Voice call host   (isVisualOnly=false, liveDestination set):  voice via SIP
 *
 * Pure function so the contract is unit-testable without React. Locked
 * down Apr-30 2026 (live-mode reliability fix).
 */
export function resolveWithVoiceCall(args: {
  callerOverride: boolean | undefined;
  isVisualOnly: boolean;
  liveDestination: string | null | undefined;
}): boolean {
  if (typeof args.callerOverride === 'boolean') {
    return args.callerOverride;
  }
  if (args.isVisualOnly) {
    return false;
  }
  const trimmed = typeof args.liveDestination === 'string'
    ? args.liveDestination.trim()
    : '';
  return trimmed.length > 0;
}

